import {
  cn,
  type DateValue,
  Popover,
  PopoverContent,
  PopoverTrigger,
  type RangeCalendarProps as Props,
  RangeCalendar,
  ScrollShadow,
} from '@heroui/react';
import { parseDate } from '@internationalized/date';
import { useState } from 'react';
import { type Control, Controller, type FieldValues, type Path, type PathValue } from 'react-hook-form';
import { FaCalendarWeek } from 'react-icons/fa';
import { EDateFilter } from '@/config/constants';
import { getDateRange } from '@/utils/date';
import { AnimatedCheck } from '../..';
import { Button } from '..';

export const CalendarInput = <T extends DateValue>({
  onChange,
  selectedFilter = EDateFilter.THIS_MONTH,
  size = 'md',
  value,
  className,
  ...props
}: Props<T> & {
  selectedFilter?: EDateFilter | null;
  size?: 'sm' | 'md' | 'lg';
  radius?: 'sm' | 'md' | 'lg';
}) => {
  const [mode, setMode] = useState<EDateFilter | null>(selectedFilter);
  const [isOpen, setIsOpen] = useState(false);

  const onValChange: Props<T>['onChange'] = (val) => {
    if (onChange) {
      onChange(val);
    }
    setMode(null);
    setIsOpen(() => false);
  };

  const onListSelect = (val: EDateFilter) => {
    const parsedVal = val;
    setMode(parsedVal);
    const range = getDateRange(Number(parsedVal));

    if (onChange) {
      onChange({ start: range.start, end: range.end } as never);
    }
    // setIsOpen(() => false);
  };

  // const maxValue = startOfMonth(now('UTC'))
  //   .add({ months: 1 })
  //   .subtract({ days: 1 })
  //   .set({ hour: 23, minute: 59, second: 59, millisecond: 999 });

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      classNames={{ content: '!px-0' }}
      radius='sm'
      triggerType='listbox'
      disableAnimation
      triggerScaleOnOpen={false}
      // shadow='sm'
      offset={10}
      placement='bottom-end'
    >
      <PopoverTrigger>
        <Button
          variant='faded'
          size={size}
          disableAnimation
          disableRipple
          endContent={<FaCalendarWeek />}
          className={cn(
            'bg-default-100 hover:border-default-400 !transition-colors duration-300',
            'bg-default-100 flex hover:!opacity-100 justify-between items-center shadow-sm',
            { 'border-default-400': isOpen },
            className,
          )}
        >
          <span className=''>
            {mode !== null
              ? EDateFilter[mode]
                  ?.replaceAll('_', ' ')
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : value?.start
                ? 'Custom'
                : 'Select'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <div className='grid grid-cols-4 divide-x-2 divide-divider'>
          <ScrollShadow size={20} hideScrollBar className='col-span-1 p-2 flex flex-col max-h-[18rem]'>
            {Object.entries(EDateFilter)
              .filter(([, val]) => typeof val === 'string')
              .map(([key, val]) => {
                const formattedValue = (val as string)
                  .replaceAll('_', ' ')
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase());

                const parsed = Number.parseInt(key, 10) as unknown as EDateFilter;
                const isSelected = mode === parsed;
                return (
                  <button
                    type='button'
                    onClick={() => onListSelect(parsed)}
                    key={key}
                    className={cn(
                      'text-start rounded-md py-1.5 px-2 my-0.5 cursor-pointer',
                      'hover:bg-default-100',
                      'flex justify-between items-center',
                      {
                        // 'border-b border-divider': !isLastItem,
                        'bg-default-200 shadow-sm': isSelected,
                      },
                    )}
                  >
                    <span className=''>{formattedValue}</span>
                    <AnimatedCheck isOpen={isSelected} />
                  </button>
                );
              })}
          </ScrollShadow>
          <div className='col-span-3'>
            <RangeCalendar
              classNames={{
                base: '!px-0 !rounded-none shadow-none',
                gridHeader: '!shadow-none',
                content: '!bg-background !shadow-none',
              }}
              color='secondary'
              radius='sm'
              value={value}
              showShadow={false}
              onChange={onValChange}
              // maxValue={maxValue}
              visibleMonths={2}
              {...props}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export interface CalendarInputProps<T extends FieldValues> extends Props<DateValue> {
  name: Path<T>;
  control: Control<T, unknown>;
  defaultValue?: PathValue<T, Path<T>>;
}

export const FormCalendarInput = <T extends FieldValues>({
  control,
  name,
  defaultValue,
  ...rest
}: CalendarInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || ({ start: undefined, end: undefined } as never)}
      render={({ field: { onChange, onBlur, value }, fieldState: { error, isDirty, invalid } }) => {
        const onHandleChange: CalendarInputProps<T>['onChange'] = (value) => {
          if (!value) {
            onChange(value);
            return;
          }
          onChange({
            start: value.start.toString(),
            end: value.end.toString(),
          });
        };

        return (
          <CalendarInput
            {...rest}
            // value={value}
            errorMessage={error?.message}
            value={
              {
                start: value.start ? parseDate(value.start) : undefined,
                end: value.end ? parseDate(value.end) : undefined,
              } as never
            }
            isInvalid={isDirty && invalid}
            onBlur={onBlur}
            onChange={onHandleChange}
          />
        );
      }}
    />
  );
};
